import { ReactElement, useContext, useState } from "react"
import Select from "react-select"
import moment from "moment/moment"
import { SingleDatePicker } from "react-dates"

import { FormLabel, Input, Modal } from "@wfp/ui"

import { EditModalProps, IReportObs, ObservationStatus } from "../../../../interfaces/markets"

import { DataUploadContext } from "../../../../providers/DataUploadContext"
import {
  useGetCommoditiesUnitByCountryQuery,
  useGetCommodityByCountryQuery,
} from "../../../../services/core/commodities"
import { useGetCurrenciesByCountryQuery } from "../../../../services/core/currencies"
import { useGetMarketsQuery } from "../../../../services/core/markets"
import { useGetPriceTypeQuery } from "../../../../services/core/priceType"

const ValidationKeys = ({ open, items, originalItems, onClose, onSubmit }: EditModalProps): ReactElement => {
  const { filters } = useContext(DataUploadContext)

  const templateItem = items[0]
  const status = templateItem?.observationStatusId

  const [form, setForm] = useState<IReportObs>({ ...templateItem })
  const [focused, setFocused] = useState<boolean>(false)

  const showAll = status === ObservationStatus.INVALID_SERIES
  const showDateField = status === ObservationStatus.INVALID_DATE
  const showMarketField = showAll || originalItems.some(item => !item.marketId)
  const showCommodityField = showAll || originalItems.some(item => !item.commodityId)
  const showUnitField = showAll || originalItems.some(item => !item.commodityUnitId)
  const showCurrencyField = showAll || originalItems.some(item => !item.currencyId)
  const showPriceTypeField = showAll || originalItems.some(item => !item.priceTypeId)

  const { data: markets, isFetching: loadingMarkets } = useGetMarketsQuery(
    { adm0code: filters?.adm0Code, skip: 0, top: 0 },
    { skip: !filters?.adm0Code },
  )
  const { data: commodities, isFetching: loadingCommodities } = useGetCommodityByCountryQuery(
    { admCode: filters?.adm0Code },
    { skip: !filters?.adm0Code },
  )
  const { data: units, isFetching: loadingUnits } = useGetCommoditiesUnitByCountryQuery(filters?.adm0Code ?? 0, {
    skip: !filters?.adm0Code,
  })
  const { data: currencies, isFetching: loadingCurrencies } = useGetCurrenciesByCountryQuery(filters?.adm0Code ?? 0, {
    skip: !filters?.adm0Code,
  })
  const { data: priceType } = useGetPriceTypeQuery()

  const validate = (): boolean => {
    return (
      (!showMarketField || !!form?.marketId) &&
      (!showCommodityField || !!form?.commodityId) &&
      // (!showUnitField || !!form?.commodityUnitId) &&
      (!showCurrencyField || !!form?.currencyId) &&
      (!showPriceTypeField || !!form?.priceTypeId) &&
      (!showDateField || !!form?.commodityPriceDate)
    )
  }

  const handleSubmit = (): void => {
    const updatedItems = items.map(item => ({
      ...item,
      marketId: showMarketField ? form?.marketId : item.marketId,
      marketName: showMarketField ? form?.marketName : item.marketName,
      commodityId: showCommodityField ? form?.commodityId : item.commodityId,
      commodityName: showCommodityField ? form?.commodityName : item.commodityName,
      commodityUnitId: showUnitField ? form?.commodityUnitId : item.commodityUnitId,
      commodityUnitName: showUnitField ? form?.commodityUnitName : item.commodityUnitName,
      currencyId: showCurrencyField ? form?.currencyId : item.currencyId,
      currencyName: showCurrencyField ? form?.currencyName : item.currencyName,
      priceTypeId: showPriceTypeField ? form?.priceTypeId : item.priceTypeId,
      priceTypeName: showPriceTypeField ? form?.priceTypeName : item.priceTypeName,
      commodityPriceDate: showDateField ? form?.commodityPriceDate : item.commodityPriceDate,
    }))

    onSubmit(updatedItems as IReportObs[])
  }

  return (
    <Modal
      lazyLoad
      open={open}
      modalHeading={items.length > 1 ? `Edit ${items.length} Items` : "Edit Item"}
      secondaryButtonText="Close"
      onRequestClose={onClose}
      primaryButtonDisabled={!validate()}
      primaryButtonText="Save"
      onRequestSubmit={handleSubmit}
    >
      <div className="space-y-4">
        {showMarketField && (
          <div>
            <FormLabel>Market</FormLabel>
            <Select
              isSearchable
              isLoading={loadingMarkets}
              placeholder="Select market"
              classNamePrefix="select"
              options={markets?.items ?? []}
              getOptionLabel={opt => opt.marketName}
              getOptionValue={opt => opt.marketID.toString()}
              value={markets?.items?.find(market => market.marketID === form?.marketId)}
              onChange={e =>
                setForm(
                  prev => ({ ...prev, marketId: e?.marketID ?? 0, marketName: e?.marketName ?? "" } as IReportObs),
                )
              }
            />
          </div>
        )}

        {showCommodityField && (
          <div>
            <FormLabel>Commodity</FormLabel>
            <Select
              isSearchable
              isLoading={loadingCommodities}
              placeholder="Select commodity"
              classNamePrefix="select"
              options={commodities ?? []}
              getOptionLabel={opt => opt.commodityName}
              getOptionValue={opt => opt.commodityId.toString()}
              value={commodities?.find(commodity => commodity.commodityId === form?.commodityId)}
              onChange={e =>
                setForm(
                  prev =>
                    ({
                      ...prev,
                      commodityId: e?.commodityId ?? 0,
                      commodityName: e?.commodityName ?? "",
                    } as IReportObs),
                )
              }
            />
          </div>
        )}

        {showUnitField && (
          <div>
            <FormLabel>Unit</FormLabel>
            <Select
              isSearchable
              isLoading={loadingUnits}
              placeholder="Select unit"
              classNamePrefix="select"
              options={units ?? []}
              getOptionLabel={opt => opt.commodityUnitName}
              getOptionValue={opt => opt.commodityUnitID.toString()}
              value={units?.find(unit => unit.commodityUnitID === form?.commodityUnitId)}
              onChange={e =>
                setForm(
                  prev =>
                    ({
                      ...prev,
                      commodityUnitId: e?.commodityUnitID ?? 0,
                      commodityUnitName: e?.commodityUnitName ?? "",
                    } as IReportObs),
                )
              }
            />
          </div>
        )}

        {showCurrencyField && (
          <div>
            <FormLabel>Currency</FormLabel>
            <Select
              isSearchable
              isLoading={loadingCurrencies}
              placeholder="Select currency"
              classNamePrefix="select"
              options={currencies ?? []}
              getOptionLabel={opt => opt.currencyName}
              getOptionValue={opt => opt.currencyID.toString()}
              value={currencies?.find(currency => currency.currencyID === form?.currencyId)}
              onChange={e =>
                setForm(
                  prev =>
                    ({ ...prev, currencyId: e?.currencyID ?? 0, currencyName: e?.currencyName ?? "" } as IReportObs),
                )
              }
            />
          </div>
        )}

        {showPriceTypeField && (
          <div>
            <FormLabel>Price type</FormLabel>
            <Select
              isSearchable
              isLoading={loadingCurrencies}
              placeholder="Select price type"
              classNamePrefix="select"
              options={priceType ?? []}
              getOptionLabel={opt => opt.priceTypeName}
              getOptionValue={opt => opt.priceTypeID.toString()}
              value={priceType?.find(price => price.priceTypeID === form?.priceTypeId)}
              onChange={e =>
                setForm(
                  prev =>
                    ({
                      ...prev,
                      priceTypeId: e?.priceTypeID ?? 0,
                      priceTypeName: e?.priceTypeName ?? "",
                    } as IReportObs),
                )
              }
            />
          </div>
        )}

        {showDateField && (
          <div className="wfp--form-item">
            <Input labelText="Price Date" invalidText="A valid value is required">
              {() => (
                <SingleDatePicker
                  displayFormat="DD/MM/YYYY"
                  hideKeyboardShortcutsPanel
                  showDefaultInputIcon
                  inputIconPosition="after"
                  numberOfMonths={1}
                  isOutsideRange={day => day.isAfter(moment(), "day")}
                  onDateChange={newDate => setForm({ ...form, commodityPriceDate: newDate?.toISOString() || "" })}
                  focused={focused}
                  onFocusChange={() => setFocused(prev => !prev)}
                  date={form.commodityPriceDate ? moment(form.commodityPriceDate) : null}
                  id="commodityPriceDate"
                />
              )}
            </Input>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ValidationKeys
